import { hasPermission } from "./permissions";
import { errorMessage, isVersionConflict } from "./api";

// Procedures and checklists: pure helpers shared by the template editor, its preview and the execution page.
// The server checks everything again (backend/procedures/rules.py); these only guide the editor and decide what to show.

export const KINDS = ["status", "text", "number", "date", "choice", "photo", "file"];
export const KIND_LABELS = {
  status: "Statut (OK / Non OK…)", text: "Texte", number: "Nombre ou mesure", date: "Date", choice: "Choix dans une liste", photo: "Photo", file: "Fichier",
};
export const KIND_SHORT = { status: "Statut", text: "Texte", number: "Nombre", date: "Date", choice: "Choix", photo: "Photo", file: "Fichier" };
export const STATUS_VALUES = ["ok", "nok", "na", "blocked"];
export const STATUS_LABELS = { ok: "OK", nok: "Non OK", na: "Non applicable", blocked: "Bloqué" };
const DEFAULT_STATUS_ANOMALIES = ["nok", "blocked"];
export const PROOFS = ["none", "photo", "file"];
export const PROOF_LABELS = { none: "Aucune", photo: "Photo exigée", file: "Fichier exigé" };
export const FILE_KINDS = ["photo", "file"];
const CONDITION_KINDS = ["status", "choice"];
export const VERSION_STATUS_LABELS = { draft: "Brouillon", published: "Publiée", deprecated: "Remplacée" };
const MAX_ITEMS = 200;
const MAX_SECTIONS = 30;

const newId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `id-${Math.random().toString(16).slice(2)}${Date.now()}`);

export function newSection(title = "") {
  return { id: newId(), title, items: [] };
}

export function newItem(kind = "status") {
  return {
    id: newId(), kind, label: "", help: null, required: true, proof: "none", options: kind === "choice" ? ["", ""] : [], unit: null, min: null, max: null,
    anomaly_values: kind === "status" ? [...DEFAULT_STATUS_ANOMALIES] : [], condition: null, estimated_minutes: null,
  };
}

// Changing the kind of an item keeps what still makes sense and resets the rest.
export function changeKind(item, kind) {
  const fresh = newItem(kind);
  return { ...fresh, id: item.id, label: item.label, help: item.help, required: item.required, estimated_minutes: item.estimated_minutes,
    proof: FILE_KINDS.includes(kind) ? "none" : item.proof, condition: item.condition };
}

export function allItems(sections) {
  return sections.flatMap((s) => s.items.map((item) => ({ ...item, sectionId: s.id, sectionTitle: s.title })));
}

// The items a condition may point at: those placed BEFORE `itemId`, of a closed kind.
export function conditionTargets(sections, itemId) {
  const out = [];
  for (const item of allItems(sections)) {
    if (item.id === itemId) break;
    if (CONDITION_KINDS.includes(item.kind)) out.push(item);
  }
  return out;
}

export function conditionValues(target) {
  if (!target) return [];
  return target.kind === "status" ? STATUS_VALUES.map((v) => ({ value: v, label: STATUS_LABELS[v] })) : target.options.filter(Boolean).map((o) => ({ value: o, label: o }));
}

// Is `item` shown given the answers so far ({item_id: {value}} or {item_id: value})? A hidden item is not asked and not required.
export function isVisible(item, answers) {
  if (!item.condition) return true;
  const raw = answers?.[item.condition.item_id];
  const value = raw && typeof raw === "object" && "value" in raw ? raw.value : raw;
  return value === item.condition.equals;
}

// Is this answer value an anomaly for this item (the server decides the same way in 10-3)?
export function isAnomaly(item, value) {
  if (value === null || value === undefined || value === "") return false;
  if (item.kind === "status" || item.kind === "choice") return (item.anomaly_values || []).includes(value);
  if (item.kind === "number") {
    const n = Number(value);
    return Number.isFinite(n) && ((item.min !== null && item.min !== undefined && n < item.min) || (item.max !== null && item.max !== undefined && n > item.max));
  }
  return false;
}

// First problem of a draft, as the editor shows it before the server is asked: {message, path} or null.
export function draftProblem(sections, { publishing = false } = {}) {
  if (sections.length > MAX_SECTIONS) return { message: `Une procédure compte au plus ${MAX_SECTIONS} sections.` };
  if (allItems(sections).length > MAX_ITEMS) return { message: `Une procédure compte au plus ${MAX_ITEMS} éléments.` };
  const earlier = new Map();
  for (const section of sections) {
    if (!section.title.trim()) return { message: "Chaque section doit avoir un titre.", path: section.id };
    if (publishing && section.items.length === 0) return { message: `La section « ${section.title} » est vide : complétez-la ou retirez-la.`, path: section.id };
    for (const item of section.items) {
      const path = `${section.id}/${item.id}`;
      if (!item.label.trim()) return { message: "Chaque élément doit avoir un libellé.", path };
      if (item.kind === "choice") {
        const options = item.options.map((o) => o.trim());
        if (options.length < 2 || options.some((o) => !o)) return { message: "Un choix demande au moins deux options remplies.", path };
        if (new Set(options.map((o) => o.toLowerCase())).size !== options.length) return { message: "Les options d'un choix doivent être différentes.", path };
      }
      if (item.kind === "number" && item.min !== null && item.max !== null && item.min !== undefined && item.max !== undefined && Number(item.min) > Number(item.max)) {
        return { message: "Le minimum doit être inférieur ou égal au maximum.", path };
      }
      if (item.condition) {
        const target = earlier.get(item.condition.item_id);
        if (!target || !CONDITION_KINDS.includes(target.kind)) return { message: "La condition doit dépendre d'un statut ou d'un choix placé avant.", path };
        if (!conditionValues(target).some((v) => v.value === item.condition.equals)) return { message: "La valeur de la condition n'existe plus.", path };
      }
      earlier.set(item.id, item);
    }
  }
  if (publishing && allItems(sections).length === 0) return { message: "Ajoutez au moins un élément avant de publier." };
  return null;
}

// What the editor sends: trimmed texts, numbers as numbers, nothing the server does not expect.
export function toPayload(sections) {
  const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
  return sections.map((s) => ({
    id: s.id, title: s.title.trim(),
    items: s.items.map((i) => ({
      id: i.id, kind: i.kind, label: i.label.trim(), help: i.help?.trim() || null, required: !!i.required,
      proof: FILE_KINDS.includes(i.kind) ? "none" : i.proof,
      ...(i.kind === "choice" ? { options: i.options.map((o) => o.trim()) } : {}),
      ...(i.kind === "number" ? { unit: i.unit?.trim() || null, min: num(i.min), max: num(i.max) } : {}),
      ...(["status", "choice"].includes(i.kind) ? { anomaly_values: i.anomaly_values } : {}),
      condition: i.condition || null,
      estimated_minutes: num(i.estimated_minutes),
    })),
  }));
}

// Remove an item, and the conditions that pointed at it (they would be refused).
export function removeItem(sections, itemId) {
  return sections.map((s) => ({
    ...s,
    items: s.items.filter((i) => i.id !== itemId).map((i) => (i.condition?.item_id === itemId ? { ...i, condition: null } : i)),
  }));
}

// Move an item up or down within its section; refused (unchanged) when it would land before an item it depends on, or after one
// that depends on it.
export function moveItem(sections, itemId, delta) {
  const next = sections.map((s) => {
    const index = s.items.findIndex((i) => i.id === itemId);
    if (index < 0) return s;
    const target = index + delta;
    if (target < 0 || target >= s.items.length) return s;
    const items = [...s.items];
    [items[index], items[target]] = [items[target], items[index]];
    return { ...s, items };
  });
  return draftProblem(next) && !draftProblem(sections) ? sections : next;
}

export function moveSection(sections, sectionId, delta) {
  const index = sections.findIndex((s) => s.id === sectionId);
  const target = index + delta;
  if (index < 0 || target < 0 || target >= sections.length) return sections;
  const next = [...sections];
  [next[index], next[target]] = [next[target], next[index]];
  return draftProblem(next) && !draftProblem(sections) ? sections : next;
}

export function versionSummary(version) {
  const parts = [`${version.item_count} élément${version.item_count > 1 ? "s" : ""}`];
  if (version.estimated_minutes) parts.push(`environ ${version.estimated_minutes} min`);
  return parts.join(" · ");
}

export function canManageProcedures(user) {
  return !!user?.features?.procedures && hasPermission(user, "procedures:manage");
}

// --- Runs within a mission -----------------------------------------------------------------------------------------------
export const RUN_STATUS_LABELS = { in_progress: "En cours", completed: "Terminée" };
export const MISSING_LABELS = { answer: "réponse manquante", proof: "preuve manquante" };

// What the person answered, in words (status label, measure with its unit).
export function valueLabel(item, value) {
  if (value === null || value === undefined || value === "") return "";
  if (item.kind === "status") return STATUS_LABELS[value] || value;
  if (item.kind === "number") return item.unit ? `${value} ${item.unit}` : String(value);
  if (item.kind === "date") {
    const [y, m, d] = String(value).split("-");
    return d ? `${d}/${m}/${y}` : String(value);
  }
  return String(value);
}

export function isAnswered(item, answer) {
  if (!answer) return false;
  if (FILE_KINDS.includes(item.kind)) return (answer.files || answer.file_ids || []).length > 0;
  return answer.value !== null && answer.value !== undefined && answer.value !== "";
}

// Shown items of a section and how many are answered (hidden items never count).
export function sectionProgress(section, answers) {
  const shown = section.items.filter((i) => isVisible(i, answers));
  return { total: shown.length, answered: shown.filter((i) => isAnswered(i, answers[i.id])).length,
    anomalies: shown.filter((i) => answers[i.id]?.anomaly).length };
}

// The whole answer sent back on every save (the server replaces the answer): `changes` override what is already there.
export function answerPayload(answer, changes = {}) {
  const base = {
    value: answer?.value ?? null,
    comment: answer?.comment ?? null,
    file_ids: (answer?.files || []).map((f) => f.id),
    performed_by: answer?.performed_by ? { kind: answer.performed_by.kind, id: answer.performed_by.id } : null,
  };
  const out = { expected_version: answer?.version || 0, ...base, ...changes };
  if (!out.performed_by) delete out.performed_by;
  return out;
}

// The follow-up task opened from an item in anomaly: the usual task form, prefilled.
export function followUpDraft(run, item, answer) {
  const lines = [
    `Pharmacie : ${run.mission.pharmacy_name}`,
    `Mission : ${run.mission.reference} · ${run.mission.title}`,
    `Procédure : ${run.label} (v${run.version_number})`,
    `Élément : ${item.label}${answer?.value != null && answer.value !== "" ? ` → ${valueLabel(item, answer.value)}` : ""}`,
    ...(answer?.comment ? [`Commentaire : ${answer.comment}`] : []),
  ];
  return { title: `Anomalie : ${item.label}`.slice(0, 200), description: lines.join("\n"), priority: "high" };
}

export function procedureErrorMessage(error, fallback = "Une erreur est survenue. Réessayez.") {
  if (!error?.response) return "Connexion impossible. Vérifiez votre réseau : votre réponse n'a pas été enregistrée.";
  if (isVersionConflict(error)) return "Un collègue vient de modifier cet élément : sa réponse est affichée, vérifiez puis recommencez.";
  return errorMessage(error, fallback);
}
